import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Heart, Leaf } from "lucide-react";
import { useLang } from "@/hooks/use-language";
import { useSmartBack } from "@/hooks/use-smart-back";
import { supabase } from "@/integrations/supabase/client";
import { getParentWeeklyPanel } from "@/lib/parent-panel.functions";
import { PANEL_COPY, parentPrompt, familyMoment } from "@/lib/parent-panel-copy";

export const Route = createFileRoute("/_authenticated/app/parent/this-week")({
  component: ParentWeekly,
});

type Panel = Awaited<ReturnType<typeof getParentWeeklyPanel>>["panels"][number];

function ParentWeekly() {
  const navigate = useNavigate();
  const goBack = useSmartBack(() => navigate({ to: "/app/parent" }));
  const { lang, dir } = useLang();
  const isAr = lang === "ar";
  const fetchPanel = useServerFn(getParentWeeklyPanel);

  const [panels, setPanels] = useState<Panel[]>([]);
  const [loading, setLoading] = useState(true);
  const [imgUrls, setImgUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchPanel();
        if (cancelled) return;
        setPanels(res.panels);
        // Best-effort signed URLs for the most recent creation per child.
        const entries: Record<string, string> = {};
        await Promise.all(
          res.panels.map(async (p) => {
            if (!p.creation?.storage_path) return;
            const { data } = await supabase.storage
              .from("creations")
              .createSignedUrl(p.creation.storage_path, 600);
            if (data?.signedUrl) entries[p.child.id] = data.signedUrl;
          }),
        );
        if (!cancelled) setImgUrls(entries);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchPanel]);

  return (
    <div dir={dir} className="px-5 py-6 max-w-2xl mx-auto pb-20">
      <button
        onClick={goBack}
        className="flex items-center gap-2 text-sm font-bold text-foreground/60 mb-5 hover:text-foreground transition-colors"
      >
        <ArrowLeft className={`size-4 ${isAr ? "rotate-180" : ""}`} />
        {isAr ? "العودة" : "Back"}
      </button>

      <div className="mb-7 animate-fade-in">
        <p className="font-display-en text-[10px] uppercase tracking-[0.32em] text-primary font-bold mb-2 flex items-center gap-1.5">
          <Leaf className="size-3" /> {PANEL_COPY.pageEyebrow[lang]}
        </p>
        <h1 className={`${isAr ? "ar-display" : "font-display-en"} text-3xl md:text-4xl font-bold tracking-tight leading-tight`}>
          {PANEL_COPY.pageTitle[lang]}
        </h1>
        <p className={`${isAr ? "ar-story" : ""} text-sm text-foreground/65 mt-2.5 max-w-md leading-relaxed`}>
          {PANEL_COPY.pageSubtitle[lang]}
        </p>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[0, 1].map((i) => (
            <div key={i} className="h-72 rounded-[28px] bg-card border-2 border-clay/30 animate-pulse" />
          ))}
        </div>
      ) : panels.length === 0 ? (
        <div className="bg-card border-2 border-dashed border-clay/40 rounded-3xl p-10 text-center">
          <p className="text-foreground/60 mb-4">
            {isAr ? "لم تتم إضافة أطفال بعد." : "No children added yet."}
          </p>
          <Link
            to="/onboarding"
            className="inline-block px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground font-bold text-sm"
          >
            {isAr ? "ابدأ" : "Begin"}
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {panels.map((p) => (
            <ChildPanel
              key={p.child.id}
              panel={p}
              isAr={isAr}
              lang={lang}
              creationUrl={imgUrls[p.child.id]}
            />
          ))}
        </div>
      )}

      <div className="flex items-start gap-3 p-5 rounded-3xl bg-clay/15 mt-8">
        <Heart className="size-4 text-primary fill-primary/30 mt-0.5 shrink-0" />
        <p className={`${isAr ? "ar-story" : ""} text-sm text-foreground/70 leading-relaxed`}>
          {PANEL_COPY.warmClose[lang]}
        </p>
      </div>
    </div>
  );
}

function ChildPanel({
  panel,
  isAr,
  lang,
  creationUrl,
}: {
  panel: Panel;
  isAr: boolean;
  lang: "en" | "ar";
  creationUrl?: string;
}) {
  const { child, week, value, between_weeks, last_completed_week, creation, reflection } = panel;
  const headingValue = value ?? (last_completed_week
    ? { emoji: "🌿", label_en: last_completed_week.value_code, label_ar: last_completed_week.value_code, code: last_completed_week.value_code }
    : null);
  const promptCode = value?.code ?? last_completed_week?.value_code ?? null;

  return (
    <article className="bg-card border-2 border-clay/30 rounded-[28px] p-5 md:p-6 shadow-sm">
      {/* Header: This week with {child} 🤍 */}
      <header className="flex items-center gap-3 mb-5">
        <div className="text-4xl">{child.avatar_emoji}</div>
        <div className="flex-1 min-w-0">
          <p className="font-display-en text-[10px] uppercase tracking-[0.28em] text-foreground/50 font-bold">
            {isAr ? "هذا الأسبوع مع" : "This week with"}
          </p>
          <h2 className={`${isAr ? "ar-display" : "font-display-en"} text-xl font-bold truncate`}>
            {child.name} <span className="text-primary">🤍</span>
          </h2>
        </div>
        <Link
          to="/app/child/$childId"
          params={{ childId: child.id }}
          className="text-[11px] font-bold text-primary/80 hover:text-primary shrink-0 px-3 py-1.5 rounded-full bg-primary/8 hover:bg-primary/12 transition-colors"
        >
          {PANEL_COPY.openChild[lang]}
        </Link>
      </header>

      {/* Between-weeks gentle pause */}
      {between_weeks && (
        <div className="bg-gradient-to-br from-secondary/10 to-clay/15 border border-secondary/20 rounded-2xl p-4 mb-4">
          <p className={`${isAr ? "ar-story" : ""} text-sm text-foreground/70 leading-relaxed`}>
            🌿 {PANEL_COPY.betweenWeeks[lang]}
          </p>
        </div>
      )}

      {/* This week's value */}
      {headingValue && (
        <section className="mb-4">
          <p className="font-display-en text-[10px] uppercase tracking-[0.28em] text-primary/70 font-bold mb-1.5">
            {PANEL_COPY.thisWeekValue[lang]}
          </p>
          <div className="flex items-center gap-3">
            <div className="size-12 rounded-2xl bg-primary/10 flex items-center justify-center text-2xl">
              {headingValue.emoji}
            </div>
            <div>
              <p className={`${isAr ? "ar-display" : "font-display-en"} text-lg font-bold leading-tight`}>
                {isAr ? headingValue.label_ar : headingValue.label_en}
              </p>
              {week && (
                <p className={`${isAr ? "ar-story" : ""} text-xs text-foreground/55 mt-0.5 leading-relaxed`}>
                  {isAr ? week.intro_ar : week.intro_en}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {!week && !between_weeks && (
        <p className={`${isAr ? "ar-story" : ""} text-sm text-foreground/65 mb-4 leading-relaxed`}>
          🌱 {PANEL_COPY.noJourneyYet[lang]}
        </p>
      )}

      {/* Parent prompt */}
      <SoftSection
        eyebrow={PANEL_COPY.parentPromptLabel[lang]}
        text={parentPrompt(promptCode, lang)}
        emoji="💬"
        isAr={isAr}
      />

      {/* Family moment */}
      <SoftSection
        eyebrow={PANEL_COPY.familyMomentLabel[lang]}
        text={familyMoment(promptCode, lang)}
        emoji="🏡"
        isAr={isAr}
      />

      {/* Recent reflection */}
      {reflection && (
        <ReflectionBlock reflection={reflection} isAr={isAr} lang={lang} />
      )}

      {/* Recent creation */}
      {creation && (
        <section className="mt-4">
          <p className="font-display-en text-[10px] uppercase tracking-[0.28em] text-foreground/50 font-bold mb-2">
            {PANEL_COPY.recentCreationLabel[lang]}
          </p>
          <div className="rounded-2xl overflow-hidden bg-clay/20 border border-clay/30 aspect-[4/3] flex items-center justify-center">
            {creationUrl && creation.mime?.startsWith("image") ? (
              <img
                src={creationUrl}
                alt={isAr ? "صنعها طفلك" : "Made by your child"}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <span className="text-4xl">🎨</span>
            )}
          </div>
        </section>
      )}
    </article>
  );
}

function SoftSection({ eyebrow, text, emoji, isAr }: { eyebrow: string; text: string; emoji: string; isAr: boolean }) {
  return (
    <section className="mt-4 p-4 rounded-2xl bg-gradient-to-br from-accent/8 to-primary/5 border border-accent/15">
      <p className="font-display-en text-[10px] uppercase tracking-[0.28em] text-foreground/50 font-bold mb-1.5 flex items-center gap-1.5">
        <span>{emoji}</span> {eyebrow}
      </p>
      <p className={`${isAr ? "ar-story" : ""} text-sm text-foreground/75 leading-relaxed`}>
        {text}
      </p>
    </section>
  );
}

function ReflectionBlock({
  reflection,
  isAr,
  lang,
}: {
  reflection: NonNullable<Panel["reflection"]>;
  isAr: boolean;
  lang: "en" | "ar";
}) {
  const p = (reflection.payload ?? {}) as any;
  let text: string | null = null;
  if (reflection.payload_kind === "mood" && p.mood) {
    text = isAr ? `شعر/شعرت بـ: ${String(p.mood)}` : `Felt: ${String(p.mood)}`;
  } else if (reflection.payload_kind === "text" && typeof p.text === "string" && p.text.trim()) {
    text = p.text.slice(0, 220);
  } else if (reflection.payload_kind === "choice" && p.choice) {
    text = isAr ? `اختار/اختارت: ${String(p.choice)}` : `Chose: ${String(p.choice)}`;
  }
  if (!text) return null;
  return (
    <section className="mt-4 p-4 rounded-2xl bg-secondary/8 border border-secondary/15">
      <p className="font-display-en text-[10px] uppercase tracking-[0.28em] text-secondary/80 font-bold mb-1.5 flex items-center gap-1.5">
        🌿 {PANEL_COPY.recentReflection[lang]}
      </p>
      <p className={`${isAr ? "ar-story" : ""} text-sm text-foreground/75 leading-relaxed italic`}>
        "{text}"
      </p>
    </section>
  );
}
